import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCrmData } from '../../contexts/CrmDataContext';
import { usePortalPath } from '../../contexts/PortalContext';
import type { CrmUser, Project, ProjectUpdate } from '../../types/crm';
import type { CompanyProjectGroup } from '../../utils/dashboardHelpers';
import { accountDetailPath } from '../../lib/portal';
import { clientById, userCanAccessClient } from '../../utils/crmHelpers';
import { projectDetailPath } from '../../utils/dashboardHelpers';
import {
  authorDisplayName,
  formatProjectUpdateTime,
  isSystemProjectUpdate,
  latestUpdateForProject,
  latestUpdatePreview,
  listClosedProjectYears,
  projectStatusModifier,
} from '../../utils/projectHelpers';
import { ProjectClosedBarChart } from './ProjectClosedBarChart';

interface DashboardProjectsByCompanyProps {
  /** Active projects grouped by company (right list). */
  groups: CompanyProjectGroup[];
  /** Full scoped project list including Completed — used for Closed-by-month chart. */
  projects: Project[];
  updates: ProjectUpdate[];
  users: CrmUser[];
}

export function DashboardProjectsByCompany({
  groups,
  projects,
  updates,
  users,
}: DashboardProjectsByCompanyProps) {
  const projectsPath = usePortalPath('projects');
  const {
    data,
    effectiveRole,
    username,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
  } = useCrmData();
  const [search, setSearch] = useState('');
  const yearOptions = useMemo(() => listClosedProjectYears(projects), [projects]);
  const [year, setYear] = useState(() => yearOptions[0] ?? new Date().getFullYear());

  useEffect(() => {
    if (yearOptions.length === 0) return;
    if (!yearOptions.includes(year)) setYear(yearOptions[0]);
  }, [yearOptions, year]);

  const activeCount = useMemo(
    () => groups.reduce((n, g) => n + g.projects.length, 0),
    [groups],
  );
  const searchTrimmed = search.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!searchTrimmed) return groups;
    return groups
      .map((group) => {
        const next = group.projects.filter((p) => {
          const haystack = [
            p.title,
            p.reference_no,
            p.status,
            group.companyName,
            group.companyCode,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(searchTrimmed);
        });
        return { ...group, projects: next };
      })
      .filter((g) => g.projects.length > 0);
  }, [groups, searchTrimmed]);

  function canOpenAccount(group: CompanyProjectGroup): boolean {
    if (group.clientId === '_unassigned') return false;
    const client = clientById(data.clients, group.clientId);
    return userCanAccessClient(
      client,
      effectiveRole,
      username,
      data.users,
      supervisorTeam,
      actorSalespersonCodes,
      departmentSlug,
    );
  }

  return (
    <div className="dashboard-layout__split-row">
      <section className="card dashboard-panel dashboard-panel--breakdown">
        <div className="dashboard-panel__head dashboard-panel__head--with-period">
          <h2 className="detail-section-title">Projects</h2>
          <label className="dashboard-period">
            <span className="dashboard-period__label">Year</span>
            <select
              className="field-input field-input--compact"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>
        <ProjectClosedBarChart projects={projects} year={year} />
      </section>

      <section className="card dashboard-panel dashboard-panel--projects-list">
        <div className="dashboard-split__toolbar">
          <label className="dashboard-split__search">
            <span className="sr-only">Search projects</span>
            <input
              type="search"
              className="field-input field-input--compact"
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </label>
          <span className="dashboard-split__count" title="Active projects">
            {activeCount}
          </span>
        </div>

        {filteredGroups.length === 0 ? (
          <p className="detail-text dashboard-panel__empty">
            {searchTrimmed ? 'No projects match your search.' : 'No active projects.'}
          </p>
        ) : (
          <div className="dashboard-company-groups">
            {filteredGroups.map((group) => (
              <details key={group.clientId} className="dashboard-company" open>
                <summary className="dashboard-company__summary">
                  <span className="dashboard-company__name">
                    {group.clientId !== '_unassigned' && canOpenAccount(group) ? (
                      <Link
                        to={accountDetailPath(group.companyCode)}
                        className="dashboard-company__link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {group.companyName}
                      </Link>
                    ) : (
                      group.companyName
                    )}
                  </span>
                  <span className="dashboard-company__meta">
                    {group.companyCode !== '—' ? group.companyCode : ''}
                    <span className="dashboard-company__count">{group.projects.length}</span>
                  </span>
                </summary>
                <ul className="dashboard-project-list">
                  {group.projects.map((p) => {
                    const lastUpdate = latestUpdateForProject(updates, p.id);
                    const preview = latestUpdatePreview(lastUpdate, 140);
                    const system = lastUpdate ? isSystemProjectUpdate(lastUpdate.content) : false;
                    return (
                      <li key={p.id} className="dashboard-project-item">
                        <Link to={projectDetailPath(p)} className="dashboard-project-row">
                          <span
                            className={`dashboard-project-row__status project-status ${projectStatusModifier(p.status)}`}
                          >
                            {p.status ?? 'Ongoing'}
                          </span>
                          <span className="dashboard-project-row__ref">{p.reference_no ?? '—'}</span>
                          <span className="dashboard-project-row__title">{p.title}</span>
                        </Link>
                        <div className="dashboard-project-row__update">
                          {lastUpdate ? (
                            <>
                              <span className="dashboard-project-row__update-meta">
                                Last update · {authorDisplayName(lastUpdate, users)}
                                {system ? ' · System' : ''}
                                {lastUpdate.created_at && (
                                  <> · {formatProjectUpdateTime(lastUpdate.created_at)}</>
                                )}
                              </span>
                              <p className="dashboard-project-row__update-text">{preview}</p>
                            </>
                          ) : (
                            <p className="dashboard-project-row__update-text dashboard-project-row__update-text--empty">
                              No updates yet
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </details>
            ))}
          </div>
        )}

        <Link to={projectsPath} className="dashboard-panel__footer-link">
          View all projects →
        </Link>
      </section>
    </div>
  );
}
